import { useCallback, useEffect, useRef, useState } from 'react';
import { MdAdd, MdContentPaste, MdOutlineErrorOutline } from 'react-icons/md';

import { Button } from '@/components/Button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  showActionToast,
  showErrorToast,
  showSuccessToast,
} from '@/features/agent/job_change_processes/lib/toast';
import useActiveUsers from '@/features/agent/active_users/hooks/useActiveUsers';
import useCandidateProfileTags from '@/features/agent/candidates/hooks/useCandidateProfileTags';
import useFetchOrganization from '@/features/agent/organizations/api/useFetchOrganization';
import type { AgentApiCandidateProfilesItem } from '@/types/serializers';
import useCancelCandidateMail from '../api/useCancelCandidateMail';
import useCreateCandidateMail from '../api/useCreateCandidateMail';
import useCreateCandidateMailTemplate from '../api/useCreateCandidateMailTemplate';
import useFetchCandidateMailTemplates from '../api/useFetchCandidateMailTemplates';
import useSearchCandidateProfiles from '../api/useSearchCandidateProfiles';
import CandidateMailPreviewModal from '../components/CandidateMailPreviewModal';
import CandidateMailSendConfirmDialog from '../components/CandidateMailSendConfirmDialog';
import CandidateMailTemplateSaveModal from '../components/CandidateMailTemplateSaveModal';
import CandidateRecipientSelectModal, {
  type CandidateRecipient,
} from '../components/CandidateRecipientSelectModal';
import MailActionBar from '../components/MailActionBar';
import MailDialog from '../components/MailDialog';
import MailRecipientsDropdown from '../components/MailRecipientsDropdown';
import ScheduleDateTimeDialog from '../components/ScheduleDateTimeDialog';
import MailVariableEditor, {
  type MailVariableEditorHandle,
} from '../components/MailVariableEditor';
import VariableInsertMenu, {
  mailMenuContentClassName,
  mailMenuItemClassName,
} from '../components/VariableInsertMenu';
import { SEND_UNDO_WINDOW_MS } from '../constants';
import useCandidateMailNavigation from '../hooks/useCandidateMailNavigation';
import { isMailSenderConfigured } from '../lib/mailSettings';
import { toMinuteIsoString } from '../lib/scheduledAt';

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

function toRecipient(candidate: AgentApiCandidateProfilesItem): CandidateRecipient {
  return {
    id: candidate.id,
    name: candidate.name,
    age: candidate.age,
    organizationUserName: candidate.organization_users?.[0]?.name ?? null,
    email: candidate.email ?? null,
    tags: candidate.tags ?? [],
  };
}

type SendConfirmation = { scheduledAt: Date | null };

export default function CandidateMailComposeTemplate() {
  const navigation = useCandidateMailNavigation();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState<CandidateRecipient[]>([]);
  const [hasNotifiedRecipientLimit, setHasNotifiedRecipientLimit] = useState(false);
  const [isRecipientModalOpen, setIsRecipientModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [sendConfirmation, setSendConfirmation] = useState<SendConfirmation | null>(null);
  const [pendingTemplateId, setPendingTemplateId] = useState<number | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedOrganizationUserId, setSelectedOrganizationUserId] = useState<number | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateSaveDestination, setTemplateSaveDestination] = useState<
    'sent' | 'scheduled' | null
  >(null);
  const bodyEditorRef = useRef<MailVariableEditorHandle>(null);
  const { mutateAsync: createCandidateMailTemplateAsync, isPending: isSavingTemplate } =
    useCreateCandidateMailTemplate();
  // isSending(React Query の isPending)は再レンダリングまで反映されないため、同期的にガードする
  const isSendingRef = useRef(false);
  const { data: organizationData } = useFetchOrganization();
  const {
    data: templates = [],
    isLoading: isLoadingTemplates,
    isError: isTemplatesError,
  } = useFetchCandidateMailTemplates();
  const { mutateAsync: createCandidateMail, isPending: isSending } = useCreateCandidateMail();
  const { mutateAsync: cancelCandidateMail } = useCancelCandidateMail();

  const undoSend = useCallback(
    async (mailId: number) => {
      try {
        await cancelCandidateMail(mailId);
        showSuccessToast('送信を取り消しました');
      } catch (error) {
        showErrorToast(
          error instanceof Error && error.message ? error.message : '送信の取り消しに失敗しました。'
        );
      }
    },
    [cancelCandidateMail]
  );
  const { data: activeUsersData } = useActiveUsers();
  const { data: candidateTagsData } = useCandidateProfileTags();
  const debouncedKeyword = useDebouncedValue(searchKeyword, 300);
  const {
    data: candidateSearchResult,
    isLoading: isCandidateSearchLoading,
    isFetching: isCandidateSearchFetching,
    isFetchingNextPage: isCandidateSearchFetchingNextPage,
    fetchNextPage: fetchCandidateSearchNextPage,
    hasNextPage: hasMoreCandidateSearchResult,
    isError: isCandidateSearchError,
  } = useSearchCandidateProfiles({
    keyword: debouncedKeyword,
    organizationUserId: selectedOrganizationUserId ?? undefined,
    customTagIds: selectedTagIds,
    enabled: isRecipientModalOpen,
    perPage: 30,
  });
  const candidateSearchErrorMessage =
    isCandidateSearchError && candidateSearchResult === undefined
      ? '求職者の取得に失敗しました。時間を置いて再度お試しください。'
      : null;
  // メールアドレス未登録の求職者には送信できないため、送信先リストから除外する
  const candidateSearchList = (candidateSearchResult?.candidates ?? []).filter(
    (candidate) => (candidate.email ?? '').trim().length > 0
  );
  const candidateSearchTotalCount = candidateSearchResult?.totalCount ?? 0;
  const isCandidateSearchRefreshing =
    isCandidateSearchFetching && !isCandidateSearchLoading && !isCandidateSearchFetchingNextPage;
  const isCandidateListEmpty =
    !isCandidateSearchLoading &&
    !isCandidateSearchFetchingNextPage &&
    candidateSearchList.length === 0 &&
    candidateSearchTotalCount === 0;
  const mailSettings = organizationData?.mail_settings ?? null;
  const previewSenderName = mailSettings?.sender ?? organizationData?.organization?.name ?? '';
  const mailRecipientLimit = organizationData?.current_contract?.plan?.mail_recipient_limit ?? null;
  const isSenderConfigured = isMailSenderConfigured(mailSettings);
  const isInteractionLocked = !isSenderConfigured;
  const isSenderSettingModalOpen = organizationData !== undefined && !isSenderConfigured;
  const isRecipientSelectionDisabled = mailRecipientLimit === 0 || isInteractionLocked;
  const isRecipientLimitReached =
    mailRecipientLimit !== null &&
    mailRecipientLimit > 0 &&
    selectedRecipients.length >= mailRecipientLimit;
  const recipientLimitStatusText =
    mailRecipientLimit === 0
      ? '送信先を選択できません。'
      : isRecipientLimitReached && mailRecipientLimit !== null
        ? `送信先を最大${mailRecipientLimit}件まで選択できます。（上限に達しています）`
        : null;
  const shouldHighlightRecipientLimit = isRecipientSelectionDisabled || isRecipientLimitReached;

  useEffect(() => {
    if (mailRecipientLimit === null || mailRecipientLimit <= 0) {
      if (hasNotifiedRecipientLimit) {
        setHasNotifiedRecipientLimit(false);
      }
      return;
    }

    if (selectedRecipients.length === mailRecipientLimit) {
      if (!hasNotifiedRecipientLimit) {
        window.alert(`送信先を最大${mailRecipientLimit}件まで選択できます。`);
        setHasNotifiedRecipientLimit(true);
      }
    } else if (selectedRecipients.length < mailRecipientLimit && hasNotifiedRecipientLimit) {
      setHasNotifiedRecipientLimit(false);
    }
  }, [hasNotifiedRecipientLimit, mailRecipientLimit, selectedRecipients.length]);

  useEffect(() => {
    if (!isRecipientModalOpen) {
      setSearchKeyword('');
      setSelectedOrganizationUserId(null);
      setSelectedTagIds([]);
    }
  }, [isRecipientModalOpen]);

  const applyTemplate = useCallback(
    (templateId: number) => {
      const template = templates.find((item) => item.id === templateId);
      if (!template) return;
      setSubject(template.subject ?? '');
      setBody(template.body ?? '');
    },
    [templates]
  );

  const handleSelectTemplate = (templateId: number) => {
    const hasExistingInput = subject.trim().length > 0 || body.trim().length > 0;
    if (hasExistingInput) {
      setPendingTemplateId(templateId);
      return;
    }
    applyTemplate(templateId);
  };

  const handleConfirmTemplateApply = () => {
    if (pendingTemplateId === null) return;
    applyTemplate(pendingTemplateId);
    setPendingTemplateId(null);
  };

  const templateMenuMessage = isLoadingTemplates
    ? 'テンプレートを読み込み中...'
    : isTemplatesError
      ? 'テンプレートを取得できません'
      : templates.length === 0
        ? '利用可能なテンプレートがありません'
        : null;

  const handleToggleRecipient = useCallback(
    (candidate: AgentApiCandidateProfilesItem) => {
      setSelectedRecipients((prev) => {
        const exists = prev.some((recipient) => recipient.id === candidate.id);
        if (exists) {
          return prev.filter((recipient) => recipient.id !== candidate.id);
        }
        if (mailRecipientLimit !== null && prev.length >= mailRecipientLimit) {
          if (mailRecipientLimit === 0) {
            window.alert('送信先を選択できません。');
          } else {
            window.alert(`送信先は最大${mailRecipientLimit}件まで選択できます。`);
          }
          return prev;
        }
        return [...prev, toRecipient(candidate)];
      });
    },
    [mailRecipientLimit]
  );

  const handleRemoveRecipient = useCallback((candidateId: number) => {
    setSelectedRecipients((prev) => prev.filter((recipient) => recipient.id !== candidateId));
  }, []);

  const handleClearRecipients = useCallback(() => {
    setSelectedRecipients([]);
  }, []);

  const handleSelectAllVisible = useCallback(() => {
    setSelectedRecipients((prev) => {
      const existingIds = new Set(prev.map((recipient) => recipient.id));
      const next = [...prev];
      let reachedLimit = false;
      for (const candidate of candidateSearchList) {
        if (existingIds.has(candidate.id)) continue;
        if (mailRecipientLimit !== null && next.length >= mailRecipientLimit) {
          reachedLimit = true;
          break;
        }
        next.push(toRecipient(candidate));
      }
      if (reachedLimit && mailRecipientLimit !== null) {
        window.alert(`送信先は最大${mailRecipientLimit}件まで選択できます。`);
      }
      return next;
    });
  }, [candidateSearchList, mailRecipientLimit]);

  const handleLoadMoreCandidates = useCallback(() => {
    if (!hasMoreCandidateSearchResult) return;
    void fetchCandidateSearchNextPage();
  }, [fetchCandidateSearchNextPage, hasMoreCandidateSearchResult]);

  const handleScheduleSubmit = (date: Date) => {
    setIsScheduleDialogOpen(false);
    setSendConfirmation({ scheduledAt: date });
  };

  const handleSendMail = async () => {
    if (isSendingRef.current || !sendConfirmation) return;
    isSendingRef.current = true;
    const { scheduledAt } = sendConfirmation;

    if (selectedRecipients.length === 0) {
      showErrorToast('送信先が選択されていません。');
      isSendingRef.current = false;
      return;
    }

    try {
      const mail = await createCandidateMail({
        subject: subject.trim(),
        body,
        candidateProfileIds: selectedRecipients.map((recipient) => recipient.id),
        scheduledAt: scheduledAt ? toMinuteIsoString(scheduledAt) : undefined,
      });
      if (scheduledAt) {
        showSuccessToast('送信を予約しました');
      } else {
        showActionToast(
          '1分後に送信します',
          '取り消す',
          () => void undoSend(mail.id),
          SEND_UNDO_WINDOW_MS
        );
      }
      setSendConfirmation(null);
      setIsPreviewOpen(false);

      const destination = scheduledAt ? 'scheduled' : 'sent';
      if (saveAsTemplate) {
        setTemplateSaveDestination(destination);
        return;
      }
      void navigation.openFolder(destination);
    } catch (error) {
      console.error(error);
      showErrorToast(
        error instanceof Error && error.message ? error.message : 'メールの送信に失敗しました。'
      );
    } finally {
      isSendingRef.current = false;
    }
  };

  const finishTemplateSave = () => {
    const destination = templateSaveDestination ?? 'sent';
    setTemplateSaveDestination(null);
    setSaveAsTemplate(false);
    void navigation.openFolder(destination);
  };

  const handleConfirmSaveTemplate = async (name: string) => {
    try {
      await createCandidateMailTemplateAsync({ name, subject: subject.trim(), body });
      showSuccessToast('テンプレートを保存しました');
    } catch (error) {
      console.error(error);
      showErrorToast('テンプレートの保存に失敗しました');
    }
    finishTemplateSave();
  };

  const isPreviewDisabled =
    subject.trim().length === 0 || body.trim().length === 0 || isInteractionLocked;

  return (
    <>
      <div className="flex min-h-[calc(100vh-60px)] w-full flex-col gap-md bg-gray p-lg pb-[94px]">
        <div className="flex flex-col gap-xxs">
          <div className="flex min-h-[54px] items-center gap-xs rounded-sm border border-solid border-border bg-white pr-xs">
            <button
              type="button"
              onClick={() => setIsRecipientModalOpen(true)}
              disabled={isRecipientSelectionDisabled}
              className="flex h-[42px] shrink-0 cursor-pointer items-center gap-xxs rounded-sm border-none bg-transparent px-sm text-button-md text-primary outline-hidden hover:bg-primary_lighten disabled:cursor-not-allowed disabled:opacity-50"
            >
              <MdAdd className="size-5" />
              送信先を選択
            </button>
            <div className="flex min-w-0 flex-1 items-center gap-xxs overflow-hidden">
              {selectedRecipients.map((recipient) => (
                <span
                  key={recipient.id}
                  className="flex h-5 shrink-0 items-center rounded-full bg-tag-blue px-sm text-tag-sm whitespace-nowrap text-tag-blue"
                >
                  {recipient.name}
                </span>
              ))}
            </div>
            {selectedRecipients.length > 0 && (
              <MailRecipientsDropdown
                items={selectedRecipients.map((recipient) => ({
                  key: recipient.id,
                  label: recipient.email
                    ? `${recipient.name} <${recipient.email}>`
                    : recipient.name,
                  onRemove: () => handleRemoveRecipient(recipient.id),
                }))}
              />
            )}
          </div>
          {recipientLimitStatusText && (
            <p
              className={`text-body-xs ${
                shouldHighlightRecipientLimit ? 'text-danger' : 'text-sub'
              }`}
            >
              {recipientLimitStatusText}
            </p>
          )}
        </div>

        <div className="flex min-h-[460px] flex-1 flex-col overflow-hidden rounded-sm border border-solid border-border bg-white focus-within:border-primary">
          <input
            aria-label="件名"
            placeholder="件名"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            disabled={isInteractionLocked}
            className="h-[54px] w-full shrink-0 border-x-0 border-t-0 border-b border-solid border-border bg-white px-sm text-body-sm text-main outline-hidden placeholder:text-sub"
          />
          <MailVariableEditor
            ref={bodyEditorRef}
            aria-label="本文"
            placeholder="本文"
            value={body}
            onChange={setBody}
            disabled={isInteractionLocked}
            className="min-h-0 flex-1"
          />
        </div>
      </div>

      <MailActionBar
        start={
          <>
            <VariableInsertMenu
              onInsert={(token) => bodyEditorRef.current?.insert(token)}
              disabled={isInteractionLocked}
            />
            <DropdownMenu modal={false}>
              <div className="group relative">
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    color="secondary"
                    size="md"
                    iconOnly
                    aria-label="テンプレートを挿入"
                    disabled={isInteractionLocked}
                    className="peer data-[state=open]:bg-gray_darken"
                  >
                    <MdContentPaste className="size-5" />
                  </Button>
                </DropdownMenuTrigger>
                <span
                  role="tooltip"
                  className="pointer-events-none absolute bottom-full left-1/2 mb-xs hidden -translate-x-1/2 rounded-sm bg-main px-xs py-xxs text-body-xs whitespace-nowrap text-white group-hover:block peer-data-[state=open]:hidden!"
                >
                  テンプレートを挿入
                </span>
              </div>
              <DropdownMenuContent
                side="top"
                align="end"
                sideOffset={7}
                className={`max-h-[240px]! w-[163px] min-w-0! overflow-y-auto ${mailMenuContentClassName}`}
              >
                {templateMenuMessage ? (
                  <div className="px-sm py-xs text-body-sm text-sub">{templateMenuMessage}</div>
                ) : (
                  templates.map((template) => (
                    <DropdownMenuItem
                      key={template.id}
                      onClick={() => handleSelectTemplate(template.id)}
                      className={mailMenuItemClassName}
                    >
                      {template.name}
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
        end={
          <>
            <Button
              type="button"
              color="secondary"
              size="md"
              onClick={() => void navigation.openFolder('sent')}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              color="primary"
              size="md"
              disabled={isPreviewDisabled}
              onClick={() => setIsPreviewOpen(true)}
            >
              プレビューで確認
            </Button>
          </>
        }
      />

      <CandidateRecipientSelectModal
        open={isRecipientModalOpen}
        onClose={() => setIsRecipientModalOpen(false)}
        searchKeyword={searchKeyword}
        onSearchKeywordChange={setSearchKeyword}
        candidates={candidateSearchList}
        selectedRecipients={selectedRecipients}
        onToggleRecipient={handleToggleRecipient}
        onClearSelections={handleClearRecipients}
        onSelectAllVisible={handleSelectAllVisible}
        isLoading={isCandidateSearchLoading}
        isRefreshing={isCandidateSearchRefreshing}
        onLoadMore={handleLoadMoreCandidates}
        hasNextPage={Boolean(hasMoreCandidateSearchResult)}
        isFetchingNextPage={isCandidateSearchFetchingNextPage}
        errorMessage={candidateSearchErrorMessage}
        isEmpty={isCandidateListEmpty}
        totalCount={candidateSearchTotalCount}
        organizationUsers={activeUsersData?.activeUsers ?? []}
        selectedOrganizationUserId={selectedOrganizationUserId}
        onSelectedOrganizationUserIdChange={setSelectedOrganizationUserId}
        tagOptions={candidateTagsData?.tags ?? []}
        selectedTagIds={selectedTagIds}
        onSelectedTagIdsChange={setSelectedTagIds}
      />
      <CandidateMailPreviewModal
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        senderName={previewSenderName}
        senderEmail={mailSettings?.reply_address ?? null}
        signature={mailSettings?.signature ?? null}
        recipients={selectedRecipients}
        subject={subject}
        body={body}
        saveAsTemplate={saveAsTemplate}
        onSaveAsTemplateChange={setSaveAsTemplate}
        onSendNow={() => setSendConfirmation({ scheduledAt: null })}
        onSchedule={() => setIsScheduleDialogOpen(true)}
        isSending={isSending}
      />
      <ScheduleDateTimeDialog
        open={isScheduleDialogOpen}
        title="送信日時を設定"
        onClose={() => setIsScheduleDialogOpen(false)}
        onSubmit={handleScheduleSubmit}
      />
      <CandidateMailSendConfirmDialog
        open={sendConfirmation !== null}
        onClose={() => setSendConfirmation(null)}
        onConfirm={() => void handleSendMail()}
        recipientCount={selectedRecipients.length}
        subject={subject.trim()}
        scheduledAt={sendConfirmation?.scheduledAt ?? null}
        isSending={isSending}
      />
      <CandidateMailTemplateSaveModal
        open={templateSaveDestination !== null}
        initialName={subject.trim().slice(0, 100)}
        onClose={finishTemplateSave}
        onConfirm={handleConfirmSaveTemplate}
        isSaving={isSavingTemplate}
      />
      <MailDialog
        open={pendingTemplateId !== null}
        onClose={() => setPendingTemplateId(null)}
        title="内容の上書き"
        footer={
          <>
            <Button
              type="button"
              color="secondary"
              size="md"
              onClick={() => setPendingTemplateId(null)}
            >
              キャンセル
            </Button>
            <Button type="button" color="primary" size="md" onClick={handleConfirmTemplateApply}>
              適用
            </Button>
          </>
        }
      >
        <p className="text-body-sm text-main">
          テンプレートを適用すると、現在の件名と本文は上書きされます。テンプレートを適用してよろしいですか？
        </p>
      </MailDialog>
      <Dialog open={isSenderSettingModalOpen} onOpenChange={() => {}}>
        <DialogContent className="flex w-full max-w-md flex-col gap-6 overflow-hidden rounded p-6 shadow-[0px_12px_32px_rgba(15,23,42,0.16)]! sm:m-2">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-full bg-[#FEE2E2]">
              <MdOutlineErrorOutline className="size-icon-md text-[#DC2626]" />
            </div>
            <DialogTitle className="text-base! font-bold! text-[#0F172A]">
              メール設定が未設定です
            </DialogTitle>
          </div>
          <p className="text-sm leading-relaxed text-[#475569]">
            メールを作成・送信するにはメール設定を完了する必要があります。メール設定の画面で送信者名と返信先を設定してください。
          </p>
          <div className="flex justify-end">
            <Button
              type="button"
              color="primary"
              size="md"
              onClick={() => void navigation.openFolder('settings')}
            >
              メール基本設定へ移動する
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
